import { useDeleteCollection, useFetchCollectionList, useFetchUsedImageCollection } from '@api';
import { CollectionSortTypeEnum, PathnameEnum, PathParamKeyEnum } from '@constant';
import { useQueryClient } from '@tanstack/react-query';
import { ICollection } from '@types';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

export const useCollection = () => {
  // 컬렉션 이름 검색 관련
  const [inputKeyword, setInputKeyword] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
  // 컬렉션 리스트 정렬 관련
  const [sortType, setSortType] = useState<CollectionSortTypeEnum>(CollectionSortTypeEnum.RECENT_MODIFIED);
  // 컬렉션 선택 모달 관련
  const [isCollectionSelectModalVisible, setIsCollectionSelectModalVisible] = useState<boolean>(false);
  // 컬렉션 생성/수정 모달 관련
  const [isCollectionUpsertModalVisible, setIsCollectionUpsertModalVisible] = useState<boolean>(false);
  const [showGoBackButton, setShowGoBackButton] = useState<boolean>(false); // '돌아가기' 버튼 visibility 분기
  const [initialCollectionName, setInitialCollectionName] = useState<string | undefined>('');
  // 컬렉션 삭제 모달 관련
  const [isCollectionDeleteModalVisible, setIsCollectionDeleteModalVisible] = useState<boolean>(false);
  // 컬렉션 수정/삭제 관련
  const [targetCollectionId, setTargetCollectionId] = useState<number | undefined>(undefined);

  const navigate = useNavigate();
  const outputBottomRef = useRef<HTMLDivElement | null>(null);
  const outputBottomRefCurrent = outputBottomRef.current;
  const queryClient = useQueryClient();
  const location = useLocation();
  const locationPathname = location.pathname;
  const isCollectionListPage = locationPathname === PathnameEnum.COLLECTION_LIST_PAGE;

  const createQueryStringForCollectionList = () => {
    const temp = [];

    temp.push(`&sortType=${sortType}`);

    if (searchKeyword && searchKeyword.trim().length > 0) {
      temp.push(`&searchKeyword=${searchKeyword.trim()}`);
    }

    const qs = temp.join('');
    return qs;
  };

  const needFetchCollectionList = isCollectionListPage; // 컬렉션 리스트 페이지에 접근시
  const { data, hasNextPage, fetchNextPage, isLoading, isFetching } = useFetchCollectionList(
    needFetchCollectionList,
    createQueryStringForCollectionList()
  );

  const needFetchUsedImageCollection =
    needFetchCollectionList &&
    !isLoading &&
    !isFetching &&
    !hasNextPage &&
    (!searchKeyword || searchKeyword.trim().length === 0);
  const { data: usedImageCollectionData } = useFetchUsedImageCollection(needFetchUsedImageCollection);

  const { mutate, isPending } = useDeleteCollection();

  const flattenCollectionList = () => {
    const dataArrLength = data?.pages.length;
    const flattenedArr: ICollection[] = [];

    if (dataArrLength) {
      let i = 0;
      while (i < dataArrLength) {
        if (data.pages[i].data.data.length > 0) {
          data.pages[i].data.data.map((item: ICollection) => {
            flattenedArr.push(item);
          });
        }
        i++;
      }
    }

    // usedImageCollectionData가 있고, 마지막 페이지까지 fetch가 완료되었고, 컬렉션 검색 결과가 아닌 경우
    if (usedImageCollectionData && !hasNextPage && (!searchKeyword || searchKeyword.trim().length === 0)) {
      const usedImageCollection = {
        ...usedImageCollectionData,
        id: -1,
        name: '사용한 이미지',
        isUsedImages: true
      };
      flattenedArr.push(usedImageCollection);
    }

    return flattenedArr;
  };

  // 엔터 입력 시 검색 요청
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'Enter': {
        handleSubmit();
        break;
      }
      default: {
        return;
      }
    }
  };

  // 검색 인풋 키워드 변경시
  const handleInputKeywordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputKeyword(e.target.value);
  };

  // 검색 키워드 지우기
  const handleClear = () => {
    setInputKeyword('');
    setSearchKeyword(null);
  };

  // 검색 요청
  const handleSubmit = () => {
    setSearchKeyword(inputKeyword);
  };

  const handleCollectionClick = (isUsedImages: boolean, collectionId?: number) => {
    if (isUsedImages) {
      navigate(PathnameEnum.USED_IMAGE_LIST_PAGE);
    } else {
      if (collectionId) {
        const path = PathnameEnum.COLLECTION_IMAGE_LIST_PAGE.replace(
          PathParamKeyEnum.COLLECTION_ID,
          collectionId.toString()
        );
        navigate(path);
      }
    }
  };

  const handleCollectionSelectModalOpen = () => {
    setIsCollectionSelectModalVisible(true);
    setShowGoBackButton(true);
  };

  const handleCollectionSelectModalClose = () => {
    setIsCollectionSelectModalVisible(false);
    setShowGoBackButton(false);
  };

  const handleCollectionUpsertModalOpen = (targetCollectionId?: number, initialCollectionName?: string) => {
    setIsCollectionSelectModalVisible(false);
    setTargetCollectionId(targetCollectionId);
    setInitialCollectionName(initialCollectionName);
    setIsCollectionUpsertModalVisible(true);
  };

  const handleGoBackToCollectionSelectModal = () => {
    setIsCollectionUpsertModalVisible(false);
    setIsCollectionSelectModalVisible(true);
  };

  const handleCollectionUpsertModalClose = () => {
    setInitialCollectionName('');
    setIsCollectionUpsertModalVisible(false);
  };

  const handleCollectionDeleteModalOpen = (collectionId: number) => {
    setTargetCollectionId(collectionId);
    setIsCollectionDeleteModalVisible(true);
  };

  const handleCollectionDeleteModalClose = () => {
    setTargetCollectionId(undefined);
    setIsCollectionDeleteModalVisible(false);
  };

  const handleCollectionDeleteConfirmClick = () => {
    if (isPending) {
      return;
    }

    if (targetCollectionId) {
      mutate(
        { collectionId: targetCollectionId },
        {
          onSuccess: () => {
            const collectionListQueryKey = ['get', `/collections${createQueryStringForCollectionList()}`];
            const previousData = queryClient.getQueryData<any>(collectionListQueryKey);

            if (previousData) {
              // 삭제된 컬렉션을 제외한 새로운 페이지 데이터 생성
              const updatedPages = previousData.pages.map((page: any) => ({
                ...page,
                data: {
                  ...page.data,
                  data: page.data.data.filter((collection: ICollection) => collection.id !== targetCollectionId)
                }
              }));

              // 빈 페이지 제거 (모든 아이템이 필터링된 페이지 제거)
              const nonEmptyPages = updatedPages.filter((page: any) => page.data.data.length > 0);

              // 캐시 업데이트
              queryClient.setQueryData(collectionListQueryKey, {
                ...previousData,
                pages: nonEmptyPages
              });

              // 캐시 무효화 (실제 API 호출은 하지 않음)
              queryClient.invalidateQueries({
                queryKey: collectionListQueryKey,
                exact: true,
                refetchType: 'none'
              });
            }

            // 모달 닫기
            handleCollectionDeleteModalClose();
          }
        }
      );
    }
  };

  // 컬렉션 리스트 다음 페이지 호출
  useEffect(() => {
    if (outputBottomRefCurrent) {
      const observerCallback: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
          //target이 화면에 관찰되고, 다음페이지가 있고, API 요청 처리중이 아니라면 다음페이지를 호출
          if (entry.isIntersecting && hasNextPage && !isLoading && !isFetching) {
            fetchNextPage();
          }
        });
      };

      //ointersection observer 인스턴스 생성
      const observer = new IntersectionObserver(observerCallback, {
        threshold: 1.0
      });

      // 타겟 관찰 시작
      observer.observe(outputBottomRefCurrent);

      // 관찰 멈춤
      return () => observer.unobserve(outputBottomRefCurrent);
    }
  }, [outputBottomRefCurrent, hasNextPage, isLoading, isFetching, fetchNextPage]);

  return {
    data,
    flattenCollectionList,
    outputBottomRef,
    createQueryStringForCollectionList,
    inputKeyword,
    handleKeyDown,
    handleInputKeywordChange,
    handleClear,
    handleSubmit,
    sortType,
    setSortType,

    handleCollectionClick,

    // 컬렉션 선택 모달 관련
    isCollectionSelectModalVisible,
    handleCollectionSelectModalOpen,
    handleCollectionSelectModalClose,

    // 컬렉션 생성/수정 모달 관련
    isCollectionUpsertModalVisible,
    showGoBackButton,
    handleCollectionUpsertModalOpen,
    handleCollectionUpsertModalClose,
    handleGoBackToCollectionSelectModal,
    targetCollectionId,
    initialCollectionName,

    // 컬렉션 삭제 관련
    isCollectionDeleteModalVisible,
    handleCollectionDeleteModalOpen,
    handleCollectionDeleteModalClose,
    handleCollectionDeleteConfirmClick
  };
};
